// The cleanup judge's reading, one row per question: the probability as a
// number and as a bar on a fixed 0–1 scale, tinted by how far it sits from
// the end a reviewer wants. No verdict and no threshold line — those are
// host configuration (plan Decision 6), and a lab that drew one would be
// inventing it. Unjudged, the same rows are drawn empty, so the page says
// what a judgement would hold rather than hiding the section.
import { cn } from "@bindery/ui/lib/utils";
import type { CleanupJudgement } from "@bindery/speech/contract";
import { CHANGES, concern, MEANING, type QuestionSpec } from "../lib/judgement";

// Three steps rather than a blend: `color-mix()` is newer than the
// workspace's browser floor, and the lab runs on the old devices it tests.
const tint = (c: number): string =>
  c < 1 / 3
    ? "bg-[var(--lab-ok)]"
    : c < 2 / 3
      ? "bg-[var(--lab-signal)]"
      : "bg-[var(--lab-rec)]";

function Row({ spec, p }: { spec: QuestionSpec; p: number | null }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_3.25rem] items-center gap-x-3 gap-y-1 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)_3.25rem]">
      <span className="text-sm leading-tight">
        {spec.label}
        <span className="lab-label ml-2 text-[0.625rem] whitespace-nowrap">
          want {spec.wanted}
        </span>
      </span>
      <span
        className={cn(
          "relative col-span-2 row-start-2 h-2.5 overflow-hidden rounded-[1px] border sm:col-span-1 sm:row-start-auto",
          p === null ? "border-dashed bg-transparent" : "bg-muted",
        )}
        role={p === null ? undefined : "meter"}
        aria-label={p === null ? undefined : spec.label}
        aria-valuemin={p === null ? undefined : 0}
        aria-valuemax={p === null ? undefined : 1}
        aria-valuenow={p ?? undefined}
      >
        {p === null ? null : (
          <span
            className={cn("absolute inset-y-0 left-0", tint(concern(spec, p)))}
            style={{ width: `${String(p * 100)}%` }}
          />
        )}
        {[0.25, 0.5, 0.75].map((at) => (
          <span
            key={at}
            className="bg-foreground/20 absolute inset-y-0 w-px"
            style={{ left: `${String(at * 100)}%` }}
          />
        ))}
      </span>
      <span
        className={cn(
          "lab-readout text-right text-sm",
          p === null && "text-muted-foreground",
        )}
      >
        {p === null ? "—" : p.toFixed(2)}
      </span>
    </li>
  );
}

export function JudgementReadout({
  judgement,
}: {
  judgement: CleanupJudgement | null;
}) {
  const p = (spec: QuestionSpec): number | null =>
    judgement === null ? null : judgement[spec.key];
  return (
    <section
      aria-label="Cleanup judgement"
      className="flex flex-col gap-3 border-t pt-3"
    >
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h3 className="lab-label text-foreground">Cleanup judgement</h3>
        {judgement === null ? (
          <span className="lab-readout text-muted-foreground text-xs">
            not judged
          </span>
        ) : (
          <>
            <span className="lab-readout text-xs">
              {judgement.judge} {judgement.version}
            </span>
            <span className="lab-readout text-muted-foreground text-xs">
              {judgement.sawPrompt
                ? "saw the question"
                : "blind to the question"}
            </span>
          </>
        )}
        <span className="lab-label ml-auto text-[0.625rem]">
          probability, 0–1 · no threshold
        </span>
      </div>
      {judgement === null ? (
        <p className="text-muted-foreground text-xs">
          No judge answered — none is configured on the route, or it failed. The
          cleaned text is returned, but nothing claims it still says what the
          child said.
        </p>
      ) : null}
      <ul className="flex flex-col gap-2">
        <Row spec={MEANING} p={p(MEANING)} />
      </ul>
      <div className="flex flex-col gap-2">
        <span className="lab-label text-[0.625rem]">
          changes the cleanup was told not to make
        </span>
        <ul className="flex flex-col gap-2">
          {CHANGES.map((spec) => (
            <Row key={spec.key} spec={spec} p={p(spec)} />
          ))}
        </ul>
      </div>
    </section>
  );
}
